import { OnInit, OnDestroy, Directive, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { PaymentStatus } from '@app/shared/enums/payment-status.enum';
import { IVisitDetails } from '@app/shared/models/visit-details.model';
import { getSalonLogoUrlOrDefault } from '@app/shared/utils/salon';
import { BookingCompletedVisitDetails } from '@app/booking-part/models/booking-completed-visit-details.model';

@Directive()
export abstract class BookingCompletedVisitDetailsDirective implements OnInit, OnDestroy {
  public visitDetails: IVisitDetails;
  public accountCreated: boolean;
  public currentLang: string;
  public bookingReturnUrl: string;
  public isRouteBySalonId: boolean;
  public shouldRedirectToSalonWebpageOnRouteBySalonId: boolean;
  public shouldShowBookAnotherVisitButton: boolean;
  public paymentUrl: string;
  public bookingCompletedVisitDetails: BookingCompletedVisitDetails;
  private salonAllowsGifts: boolean;
  protected route = inject(ActivatedRoute);
  protected translate = inject(TranslateService);
  protected masterSubscription = new Subscription();

  ngOnInit(): void {
    this.setupData();
    this.listenToLanguageChange();
    const routeSub = this.route.queryParams.subscribe(params => {
      this.paymentUrl = params.paymentPageUrl;
    });
    this.masterSubscription.add(routeSub);
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  private setupData(): void {
    this.visitDetails = this.route.snapshot.data.visitDetails;
    if (!this.visitDetails) {
      return;
    }

    this.salonAllowsGifts = this.visitDetails.salonAllowsGifts;
    this.accountCreated = this.route.snapshot.queryParams.accountCreated === 'true';
    this.bookingReturnUrl = this.visitDetails.bookingReturnUrl;
    this.isRouteBySalonId = this.visitDetails.wasRoutedBySalonId;

    const treatments = this.visitDetails.treatments.map((treatment) => ({
        treatmentName: treatment.treatmentName,
        isDurationVisible: treatment.isDurationVisible,
        durationInMinutes: treatment.durationInMinutes,
        isPriceVisible: treatment.isPriceVisible,
        price: treatment.price
      }));

    const areAllPricesVisible = this.visitDetails.treatments.every(treatment => treatment.isPriceVisible === true);
    const totalPrice = areAllPricesVisible ? this.visitDetails.treatments.reduce((acc, value) => acc + parseFloat(value.price), 0) : null;

    this.bookingCompletedVisitDetails = {
      accountCreated: this.accountCreated,
      salonName: this.visitDetails.salonName,
      salonLogoUrl: getSalonLogoUrlOrDefault(this.visitDetails.salonLogoUrl),
      visitDate: this.visitDetails.visitDate,
      paymentType: this.visitDetails.paymentType,
      paymentSuccessful: this.visitDetails.paymentStatus === PaymentStatus.Completed,
      bookingReturnUrl: this.bookingReturnUrl,
      isRouteBySalonId: this.isRouteBySalonId,
      treatments,
      totalPrice,
    };

    const userHasExhaustedTheGiftsPool =
      Array.isArray(this.route.snapshot.data.gifts)
      && this.route.snapshot.data.gifts.length === 0;

    const giftsOfferIsAvailable = this.salonAllowsGifts && !userHasExhaustedTheGiftsPool;

    this.shouldRedirectToSalonWebpageOnRouteBySalonId =
      this.visitDetails.canRedirectToSalonWebpageOnRouteBySalonId
      && !giftsOfferIsAvailable;

    this.shouldShowBookAnotherVisitButton = !giftsOfferIsAvailable;
  }

  private listenToLanguageChange(): void {
    this.currentLang = this.translate.currentLang;
    const langChangeSub = this.translate.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.currentLang = event.lang;
      });
    this.masterSubscription.add(langChangeSub);
  }
}
